<template>
    <div
        v-if="remaining.length"
        ref="reference"
        v-on-click-outside="closeDropDown"
        class="flex dropdown-button"
    >
        <UButton
            :color
            :variant
            class="rounded-r-none mr-0.5"
            :class="first.classList"
            :data-cy="first.dataCy"
            :disabled="first.disabled"
            @click="optionClicked(first)"
        >
            <i v-if="first.icon" class="inline-block" :class="first.icon"/>
            {{ first.label }}
        </UButton>
        <UButton
            class="rounded-l-none p-1"
            :class="first.classList"
            :color
            :variant
            data-cy="caret-down-button"
            :disabled="first.disabled"
            @click="visible = !visible"
        >
            <i class="i-ph-caret-down inline-block transition-all" :class="visible ? '-rotate-180': 'rotate-0'"/>
        </UButton>
        <div
            v-if="visible"
            ref="floating"
            :style="floatingStyles"
            class="flex z-1 bg-base-100 flex-col gap-0.5 my-0.5 shadow-lg"
        >
            <UButton
                v-for="option in remaining"
                :key="option.label"
                class="whitespace-nowrap first-of-type:rounded-t last-of-type:rounded-b"
                :color
                :variant
                :class="option.classList"
                :disabled="option.disabled"
                :data-cy="option.dataCy"
                style="flex-wrap: nowrap"
                @click="optionClicked(option)"
            >
                <i v-if="option.icon" class="inline-block" :class="option.icon"/>
                {{ option.label }}
            </UButton>
        </div>
    </div>
    <UButton
        v-else-if="first"
        :color
        :variant
        :class="first.classList"
        :disabled="first.disabled"
        :data-cy="first.dataCy"
        @click="buttonClickHandler"
    >
        <i v-if="first.icon" :class="first.icon" class="inline-block"/>{{ first.label }}
    </UButton>
</template>

<script setup lang="ts">
import {computed, PropType, ref} from 'vue'
import {autoPlacement, useFloating} from '@floating-ui/vue'
import {vOnClickOutside} from '@vueuse/components'
import {DropDownButtonOption} from '@/components/dropdownButton/DropDownButtonOption.type'

const reference = ref(null)
const floating = ref(null)
const {floatingStyles, placement} = useFloating(reference, floating, {
    middleware: [autoPlacement({
        alignment: 'start',
        allowedPlacements: ['top-end', 'top-start', 'bottom-end', 'bottom-start']
    })]
})
const visible = ref(false)

const props = defineProps({
    options: {
        type: Array as PropType<Array<DropDownButtonOption>>,
        validator: (options: Array<DropDownButtonOption>) => options.length > 0,
        required: true
    },
    reverseTopPlacedOptions: {
        type: Boolean,
        default: false
    },
    color: {
        type: String,
        default: 'neutral'
    },
    variant: {
        type: String,
        validator(value: unknown): boolean {
            return ['outline', 'solid', 'ghost'].includes(value as string)
        },
        default: 'outline'
    }
})

const isTopPlacement = computed(() => placement.value.startsWith('top'))

const first = computed(() => props.options[0] || {label: 'ERROR: No option'})
const remaining = computed(() => {
    const remaining = [...props.options].slice(1)
    if (props.reverseTopPlacedOptions && isTopPlacement.value) {
        remaining.reverse()
    }
    return remaining
})

const buttonClickHandler = () => {
    if (first.value && first.value.onClick) {
        first.value.onClick()
    }
}

const optionClicked = (option: DropDownButtonOption) => {
    option.onClick && option.onClick()
    closeDropDown()
}

const closeDropDown = () => {
    visible.value = false
}
</script>